<script setup lang="ts">
import { ref, computed, nextTick, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useConfirmResetPassword } from '@/mutations/auth.mutations';

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();
const { confirmReset, isPending } = useConfirmResetPassword();

const OTP_LENGTH = 6;
const digits = ref<string[]>(Array(OTP_LENGTH).fill(''));
const inputRefs = ref<HTMLInputElement[]>([]);
const error = ref('');
const email = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const showPassword = ref(false);

onMounted(() => {
  email.value = sessionStorage.getItem('reset_email') ?? '';
  if (!email.value) router.replace('/forgot-password');
  nextTick(() => inputRefs.value[0]?.focus());
});

const isComplete = computed(() => digits.value.every((d) => d !== ''));
const otpCode = computed(() => digits.value.join(''));
const canSubmit = computed(
  () =>
    isComplete.value &&
    newPassword.value.length >= 6 &&
    newPassword.value === confirmPassword.value,
);

function generatePassword() {
  const chars =
    'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*';
  let pwd = '';
  const arr = new Uint8Array(14);
  crypto.getRandomValues(arr);
  arr.forEach((b) => {
    pwd += chars[b % chars.length];
  });
  newPassword.value = pwd;
  confirmPassword.value = pwd;
  showPassword.value = true;
}

function focusAt(index: number) {
  nextTick(() => inputRefs.value[index]?.focus());
}

function onInput(index: number, e: Event) {
  const val = (e.target as HTMLInputElement).value.replace(/\D/g, '');
  digits.value[index] = val.slice(-1);
  if (val && index < OTP_LENGTH - 1) focusAt(index + 1);
}

function onKeydown(index: number, e: KeyboardEvent) {
  if (e.key === 'Backspace' && !digits.value[index] && index > 0) {
    digits.value[index - 1] = '';
    focusAt(index - 1);
  }
}

function onPaste(e: ClipboardEvent) {
  const nums = (e.clipboardData?.getData('text') ?? '')
    .replace(/\D/g, '')
    .slice(0, OTP_LENGTH)
    .split('');
  nums.forEach((n, i) => {
    digits.value[i] = n;
  });
  focusAt(Math.min(nums.length, OTP_LENGTH - 1));
  e.preventDefault();
}

async function handleSubmit() {
  if (!canSubmit.value) return;
  if (newPassword.value !== confirmPassword.value) {
    error.value = 'Les mots de passe ne correspondent pas.';
    return;
  }
  error.value = '';
  try {
    await confirmReset({
      email: email.value,
      otp: otpCode.value,
      newPassword: newPassword.value,
    });
    sessionStorage.removeItem('reset_email');
    await router.push('/reset-success');
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    error.value =
      (Array.isArray(msgs) ? msgs[0] : msgs) || t('auth.otp.errorDefault');
    digits.value = Array(OTP_LENGTH).fill('');
    focusAt(0);
  }
}
</script>

<template>
  <div class="otp">
    <div class="otp__icon-wrap"><i class="pi pi-mobile otp__icon" /></div>

    <h2 class="otp__title">{{ t('auth.otp.title') }}</h2>
    <p class="otp__subtitle">
      {{ t('auth.otp.subtitle', { email: email || '…' }) }}
    </p>

    <div v-if="error" class="otp__error">
      <i class="pi pi-exclamation-circle" /> {{ error }}
    </div>

    <div class="otp__inputs" @paste.prevent="onPaste">
      <input
        v-for="(_, i) in digits"
        :key="i"
        :ref="
          (el) => {
            if (el) inputRefs[i] = el as HTMLInputElement;
          }
        "
        class="otp__digit"
        type="text"
        inputmode="numeric"
        maxlength="1"
        :value="digits[i]"
        @input="onInput(i, $event)"
        @keydown="onKeydown(i, $event)"
      />
    </div>

    <div class="otp__pw-section">
      <div class="otp__field">
        <div class="otp__label-row">
          <label class="otp__label" for="otp-pw">Nouveau mot de passe</label>
          <button
            type="button"
            class="otp__gen-btn"
            @click="generatePassword"
            tabindex="-1"
          >
            <i class="pi pi-refresh" /> Générer
          </button>
        </div>
        <div class="otp__input-wrap">
          <input
            id="otp-pw"
            class="otp__input"
            :type="showPassword ? 'text' : 'password'"
            v-model="newPassword"
            placeholder="Min. 6 caractères"
            autocomplete="new-password"
          />
          <button
            type="button"
            class="otp__eye"
            @click="showPassword = !showPassword"
            tabindex="-1"
          >
            <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" />
          </button>
        </div>
      </div>

      <div class="otp__field">
        <label class="otp__label" for="otp-pw2"
          >Confirmer le mot de passe</label
        >
        <div class="otp__input-wrap">
          <input
            id="otp-pw2"
            class="otp__input"
            :type="showPassword ? 'text' : 'password'"
            v-model="confirmPassword"
            placeholder="Répétez le mot de passe"
            autocomplete="new-password"
            :class="{
              'otp__input--error':
                confirmPassword && newPassword !== confirmPassword,
            }"
          />
        </div>
        <span
          v-if="confirmPassword && newPassword !== confirmPassword"
          class="otp__pw-hint"
        >
          Les mots de passe ne correspondent pas
        </span>
      </div>
    </div>

    <button
      class="otp__btn"
      :disabled="!canSubmit || isPending"
      @click="handleSubmit"
    >
      <span v-if="isPending" class="otp__spinner" />
      <span v-else>{{ t('auth.otp.submit') }}</span>
    </button>

    <p class="otp__resend">
      {{ t('auth.otp.resend') }}
      <RouterLink to="/forgot-password" class="otp__resend-btn">{{
        t('auth.otp.resendBtn')
      }}</RouterLink>
    </p>

    <RouterLink to="/forgot-password" class="otp__back">{{
      t('auth.otp.back')
    }}</RouterLink>
  </div>
</template>

<style scoped>
.otp {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.125rem;
  text-align: center;
  width: 100%;
}
.otp__icon-wrap {
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 1.25rem;
  background: rgba(255, 214, 10, 0.15);
  border: 1px solid rgba(255, 214, 10, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}
.otp__icon {
  font-size: 2rem;
  color: #c4a000;
}
.otp__title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface, #191c1d);
  letter-spacing: -0.01em;
}
.otp__subtitle {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant, #564335);
  max-width: 300px;
  line-height: 1.6;
}
.otp__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  background: var(--nxt-error-container, #ffdad6);
  color: var(--nxt-on-error-container, #93000a);
  font-size: 0.875rem;
  width: 100%;
  text-align: left;
}
.otp__inputs {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}
.otp__digit {
  width: 50px;
  height: 58px;
  border-radius: 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant, rgba(147, 130, 130, 0.2));
  background: rgba(255, 255, 255, 0.6);
  color: var(--nxt-on-surface, #191c1d);
  font-size: 1.375rem;
  font-weight: 700;
  font-family: inherit;
  text-align: center;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  box-sizing: border-box;
}
[data-theme='dark'] .otp__digit {
  background: rgba(255, 255, 255, 0.05);
  color: var(--nxt-on-surface);
}
.otp__digit:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.18);
}
[data-theme='dark'] .otp__icon-wrap {
  background: rgba(255, 214, 10, 0.1);
  border-color: rgba(255, 214, 10, 0.2);
}
[data-theme='dark'] .otp__icon {
  color: #ffd60a;
}
.otp__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  height: 54px;
  border-radius: 9999px;
  border: none;
  background: linear-gradient(135deg, #ffd60a 0%, #f5c500 100%);
  box-shadow:
    0 4px 20px rgba(255, 214, 10, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.4) inset;
  color: #0b1320;
  font-size: 1rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  letter-spacing: -0.01em;
  transition:
    box-shadow 0.2s,
    transform 0.1s,
    opacity 0.15s;
}
.otp__btn:hover {
  box-shadow:
    0 6px 28px rgba(255, 214, 10, 0.55),
    0 1px 0 rgba(255, 255, 255, 0.4) inset;
}
.otp__btn:active {
  transform: scale(0.985);
}
.otp__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.otp__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(11, 19, 32, 0.2);
  border-top-color: #0b1320;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.otp__pw-section {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  width: 100%;
}
.otp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  text-align: left;
}
.otp__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.otp__label {
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--nxt-on-surface-variant, #564335);
}
.otp__gen-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid var(--nxt-outline-variant);
  background: none;
  font-size: 0.6875rem;
  font-weight: 700;
  font-family: inherit;
  color: var(--nxt-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}
.otp__gen-btn:hover {
  background: rgba(0, 0, 0, 0.06);
}
[data-theme='dark'] .otp__gen-btn {
  color: #ffd60a;
  border-color: rgba(255, 214, 10, 0.3);
}
[data-theme='dark'] .otp__gen-btn:hover {
  background: rgba(255, 214, 10, 0.1);
}
.otp__input-wrap {
  position: relative;
}
.otp__input {
  width: 100%;
  height: 52px;
  padding: 0 2.75rem 0 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(147, 130, 130, 0.2));
  background: rgba(255, 255, 255, 0.6);
  color: var(--nxt-on-surface, #191c1d);
  font-size: 0.9375rem;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
.otp__input:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.18);
}
.otp__input::placeholder {
  color: var(--nxt-on-surface-muted, #938282);
}
.otp__input--error {
  border-color: var(--nxt-error, #ba1a1a) !important;
}
.otp__eye {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: var(--nxt-on-surface-variant);
  font-size: 1rem;
}
.otp__pw-hint {
  font-size: 0.75rem;
  color: var(--nxt-error, #ba1a1a);
}
[data-theme='dark'] .otp__input {
  background: rgba(255, 255, 255, 0.05);
  color: var(--nxt-on-surface);
}

.otp__resend {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted, #938282);
}
.otp__resend-btn {
  color: rgba(11, 19, 32, 0.6);
  font-weight: 600;
  text-decoration: none;
  transition: color 0.15s;
}
.otp__resend-btn:hover {
  color: #0b1320;
}
.otp__back {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(11, 19, 32, 0.4);
  text-decoration: none;
  transition: color 0.15s;
}
.otp__back:hover {
  color: #0b1320;
}
[data-theme='dark'] .otp__resend {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .otp__resend-btn {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .otp__resend-btn:hover {
  color: rgba(255, 255, 255, 0.8);
}
[data-theme='dark'] .otp__back {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .otp__back:hover {
  color: rgba(255, 255, 255, 0.7);
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
